/**
 * File preview component with modal display
 */

import { forwardRef } from 'react';
import { Modal, Stack, Text, Group, Button, Card } from '@/components/ui';
import { IconDownload } from '@tabler/icons-react';
import { formatFileSize, formatTimeAgo } from '../../utils/formatting';
import { isImageFile, isVideoFile, isAudioFile, isPdfFile } from '../utils';
import type { FilePreviewProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * File preview component for viewing files in a modal
 *
 * @example
 * ```tsx
 * <FilePreview
 *   file={file}
 *   onClose={() => setPreviewOpen(false)}
 *   showDownload
 *   showMetadata
 * />
 * ```
 */
export const FilePreview = forwardRef<HTMLDivElement, FilePreviewProps>(
  (props, ref) => {
    const {
      file,
      onClose,
      showDownload = true,
      showMetadata = true,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleDownload = () => {
      if (file.url) {
        window.open(file.url, '_blank');
      }
    };

    const renderPreview = () => {
      if (!file.url) {
        return (
          <Card variant="subtle" p="xl">
            <Text c="dimmed" ta="center" style={accessibilityStyles}>
              Preview not available
            </Text>
          </Card>
        );
      }

      // Image preview
      if (isImageFile(file.mimeType)) {
        return (
          <img
            src={file.url}
            alt={file.fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        );
      }

      // Video preview
      if (isVideoFile(file.mimeType)) {
        return (
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: '8px',
            }}
          >
            <source src={file.url} type={file.mimeType} />
            Your browser does not support video playback.
          </video>
        );
      }

      // Audio preview
      if (isAudioFile(file.mimeType)) {
        return (
          <Card variant="elevated" p="xl">
            <Stack gap="md" align="center">
              <Text size="lg" fw={500} style={accessibilityStyles}>
                {file.fileName}
              </Text>
              <audio controls style={{ width: '100%', maxWidth: 400 }}>
                <source src={file.url} type={file.mimeType} />
                Your browser does not support audio playback.
              </audio>
            </Stack>
          </Card>
        );
      }

      // PDF preview
      if (isPdfFile(file.mimeType)) {
        return (
          <iframe
            src={file.url}
            title={file.fileName}
            style={{
              width: '100%',
              height: '70vh',
              border: 'none',
              borderRadius: '8px',
            }}
          />
        );
      }

      // Default: no preview available
      return (
        <Card variant="subtle" p="xl">
          <Stack gap="md" align="center">
            <Text c="dimmed" style={accessibilityStyles}>Preview not available for this file type</Text>
            <Button
              variant="gradient"
              gradientVariant="primary"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
            >
              Download File
            </Button>
          </Stack>
        </Card>
      );
    };

    return (
      <Modal
        opened={true}
        onClose={onClose || (() => {})}
        size="xl"
        title={file.fileName}
        centered
      >
        <Stack gap="md" ref={ref}>
          {renderPreview()}

          {showMetadata && (
            <Card variant="subtle" p="sm">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>
                    File Size:
                  </Text>
                  <Text size="sm" style={accessibilityStyles}>{formatFileSize(file.fileSize)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>
                    Type:
                  </Text>
                  <Text size="sm" style={accessibilityStyles}>{file.mimeType}</Text>
                </Group>
                {file.uploadedAt && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed" style={accessibilityStyles}>
                      Uploaded:
                    </Text>
                    <Text size="sm" style={accessibilityStyles}>{formatTimeAgo(file.uploadedAt)}</Text>
                  </Group>
                )}
                {file.uploadedBy && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed" style={accessibilityStyles}>
                      Uploaded By:
                    </Text>
                    <Text size="sm" style={accessibilityStyles}>{file.uploadedBy}</Text>
                  </Group>
                )}
              </Stack>
            </Card>
          )}

          {showDownload && (
            <Group justify="flex-end">
              <Button
                variant="light"
                gradientVariant="primary"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}
              >
                Download
              </Button>
              {onClose && (
                <Button variant="subtle" onClick={onClose}>
                  Close
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </Modal>
    );
  }
);

FilePreview.displayName = 'FilePreview';

export default FilePreview;
